'use client'

import { useCallback, useEffect, useRef, type ReactNode } from 'react'
import type { useSantaVoicePlayer } from '@/hooks/use-santa-voice-player'
import type { getSantaChatMessages } from '@/lib/i18n/locale-messages/namespaces/santa-chat'
import type { ChatEntry, ChatState } from '@/lib/santa-chat/chat-state'
import { CallCtaCard } from './call-cta-card'
import { ChatHeader, type ChatHeaderActions } from './chat-header'
import { ChatHistory } from './chat-history'
import { Composer, type VoiceInput } from './composer'
import { SantaVideoPanel } from './santa-video-panel'

export type SantaChatCopy = ReturnType<typeof getSantaChatMessages>['chat']
export type CallSource = 'card' | 'header' | 'panel'
type Voice = ReturnType<typeof useSantaVoicePlayer>

/** Lo que se puede volver a escuchar: una nota del historial o el aviso de un turno sin guardar. */
type Replayable = Pick<ChatEntry, 'id' | 'gesture'>

/** La llamada que ofrece el panel («llámame» con su botón), solo mientras el chat la está ofreciendo. */
function panelCall(state: ChatState, label: string, onCall: () => void) {
  return state.callCta ? { label, onCall } : null
}

/** La voz que bloqueó el navegador, esté en el historial o en el aviso de un turno sin guardar. */
function blockedReplayable(state: ChatState): Replayable | undefined {
  if (state.blockedId === null) return undefined
  if (state.fallbackNotice?.id === state.blockedId) return state.fallbackNotice
  return state.entries.find((entry) => entry.id === state.blockedId)
}

/**
 * Un callback estable que siempre llama a la última versión del que se pasa:
 * las burbujas memoizadas no se repintan porque quien monta la pantalla pase
 * una función nueva en cada render.
 */
function useStableCallback<Args extends unknown[]>(callback: (...args: Args) => void) {
  const latest = useRef(callback)
  useEffect(() => {
    latest.current = callback
  })
  return useCallback((...args: Args) => latest.current(...args), [])
}

export interface SantaChatScreenProps {
  state: ChatState
  copy: SantaChatCopy
  voice: Voice
  header: Omit<ChatHeaderActions, 'call'>
  /** Bajo la cabecera (p. ej. la confirmación de borrado). */
  headerExtra?: ReactNode
  onCall: (source: CallSource) => void
  onSendText: (text: string) => void
  /** Qué hace el micrófono: grabar una nota, o (chat público) avisar sin grabar. */
  voiceInput: VoiceInput
  onResend: (localId: string) => void
  /** Bajo el historial, antes de la tarjeta de llamar (p. ej. el error al borrar). */
  notices?: ReactNode
  /** Tras la tarjeta de llamar, sobre el compositor: el aviso del estado (tope, descanso, error al abrir…). */
  statusNotice?: ReactNode
  /**
   * Antes de entrar al chat (el consentimiento del chat público): ocupa la
   * columna del chat. El panel con Papá Noel sigue montado al cambiar de una a
   * otra, así que no pierde lo que ya tenía cargado.
   */
  gate?: ReactNode
  /** Sobre la pantalla: PIN, diálogos. */
  overlay?: ReactNode
  /** Una barra del sitio encima de todo (el chat público: dónde estás, papanoel.chat). */
  siteBar?: ReactNode
}

interface ChatColumnProps extends Omit<SantaChatScreenProps, 'gate' | 'overlay' | 'siteBar'> {
  onPlay: (entry: Replayable) => void
  /** Hay una barra del sitio encima: la muesca ya la cubre ella. */
  underSiteBar: boolean
}

/** Cabecera, historial, avisos y compositor: la columna del chat. */
function ChatColumn({ state, copy, voice, header, headerExtra, onCall, onSendText, voiceInput, onResend, onPlay, notices, statusNotice, underSiteBar }: ChatColumnProps) {
  return (
    <>
      <div className="order-first flex shrink-0 flex-col">
        <ChatHeader
          labels={copy}
          santaRecording={state.santaRecording}
          actions={{ ...header, call: () => onCall('header') }}
          safeAreaTop={!underSiteBar}
        />
        {headerExtra}
      </div>
      <ChatHistory
        entries={state.entries}
        playingId={state.playingId}
        loadingId={state.loadingId}
        clock={voice.clock}
        santaRecording={state.santaRecording}
        labels={copy}
        onPlay={onPlay}
        onResend={onResend}
        resendDisabled={state.santaRecording || state.limitReached || state.opening !== 'ready'}
      />
      {notices}
      {state.callCta && <CallCtaCard label={copy.callCta} onCall={() => onCall('card')} />}
      {statusNotice}
      <Composer
        labels={copy}
        disabled={state.limitReached || state.opening !== 'ready'}
        busy={state.santaRecording}
        firstTime={state.opening === 'ready' && !state.entries.some((entry) => entry.author === 'child')}
        onSendText={onSendText}
        voice={voiceInput}
      />
    </>
  )
}

/**
 * La pantalla del chat de notas de voz con Papá Noel, sin saber de dónde salen
 * las respuestas: la monta el chat del niño (con cuenta) y el chat público. Lo
 * que cambia entre los dos entra por props; el aspecto y el comportamiento, no.
 */
export function SantaChatScreen({ gate, overlay, siteBar, onResend, ...column }: SantaChatScreenProps) {
  const { state, copy, voice, onCall } = column
  const blocked = blockedReplayable(state)
  // Stable, so the memoized bubbles only re-render when their own entry or `playing` changes.
  const replay = useCallback((entry: Replayable) => void voice.replay(entry.id, entry.gesture), [voice])
  // ⏸ on the note that is playing pauses it; any other note plays.
  const togglePlay = useCallback((entry: Replayable) => void voice.toggle(entry.id, entry.gesture), [voice])
  // Whoever mounts the screen may pass a new function each render: the bubbles still get a stable one.
  const resend = useStableCallback(onResend)

  return (
    // Móvil: cabecera, panel y chat en columna. Desde `lg`: panel a la izquierda y el chat
    // (cabecera, historial, compositor) a la derecha. En móvil la columna del chat es
    // `contents`, así que su cabecera sube por encima del panel con `order-first`.
    <main className="fixed inset-0 flex flex-col bg-background text-foreground">
      {siteBar}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <SantaVideoPanel
          gesture={state.gesture}
          gestureTurn={state.gestureTurn}
          speaking={state.speakingId !== null}
          call={panelCall(state, copy.callCta, () => onCall('panel'))}
          unblockLabel={blocked ? copy.listen : undefined}
          onUnblock={blocked ? () => replay(blocked) : undefined}
        />
        {gate ? (
          <div className="flex flex-1 flex-col justify-center overflow-y-auto lg:min-w-0">{gate}</div>
        ) : (
          <div className="contents lg:flex lg:min-w-0 lg:flex-1 lg:flex-col">
            <ChatColumn {...column} onResend={resend} onPlay={togglePlay} underSiteBar={siteBar !== undefined} />
          </div>
        )}
      </div>
      {overlay}
    </main>
  )
}
